/* Libraries */
import * as _ from "lodash";
import emitter from "@/emitter";
import moment from "moment";

/* Enums */
import ChainTypes from "@/enums/ChainTypes";
import EditCodes from "@/enums/EditCodes";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import PlaylistTypes from "@/enums/PlaylistTypes";

const dragAndDropMixin = {
	data() {
		return {
			emptyInsertThreshold: 25,   //px, distance mouse must be from empty sortable to insert drag element into it
			pendingInsertedEventsPositions: [],
			targetElementToExpand: null,
			throttled: null,
			overrideSegueTransitionsModal: {
				open: false,
				resolve: null
			}
		}
	},

	methods: {
		setDraggedEvent(event) {
			this.draggedEvent = event;
		},

		setDropTargetEvent(event) {
			this.dropTargetEvent = event;
		},

		onDragItem(ev, originalEvent, draggedElement) {
			const targetContainer = this.getContainer(ev.originalEvent);
			const targetComponent = this.getTargetComponent(targetContainer);
			const targetElement = Object.values(originalEvent.target.classList).some(c => c.includes('event')) ?
			                      originalEvent.target :
			                      document.querySelector(`[data-drop-container=${targetContainer}] .${targetContainer}-event:last-of-type`);

			if (draggedElement) {
				if (targetContainer === "hotkeys") {
					if (ev.related.dataset.row && ev.related.dataset.column) {      // if target hotkey page is not locked
						this.draggedElementData.SequenceNumber = `${ev.related.dataset.row}.${ev.related.dataset.column}`;
						targetComponent.toggleHighlightDroppableContainer(this.draggedElementData, true);
					}

					emitter.emit("playlist-set-dragged-event", null);
					emitter.emit("playlist-set-drop-target-event", null);
					if (this.getTargetComponent("log")) {
						this.getTargetComponent("log").setDropTargetEvent(null);
						this.getTargetComponent("log").setDraggedEvent(null);
					}

					this.cancelThrottled();
				}
				else if (targetContainer === "playlist") {
					this.handleOnDragAction(
						targetComponent,
						targetContainer,
						draggedElement,
						targetElement
					);

					if (this.getTargetComponent("log")) {
						this.getTargetComponent("log").setDropTargetEvent(null);
						this.getTargetComponent("log").setDraggedEvent(null);
					}
				}
				else if (targetContainer === "log") {
					this.handleOnDragAction(
						targetComponent,
						targetContainer,
						draggedElement,
						targetElement
					);

					emitter.emit("playlist-set-dragged-event", null);
					emitter.emit("playlist-set-drop-target-event", null);
				}
				else {
					this.cancelThrottled();
				}
			}
		},

		async onDropItem(ev, needToCreateEvent = false) {
			if (this.draggedElementData) {
				const targetContainer = this.getContainer(ev.originalEvent);
				const targetComponent = this.getTargetComponent(targetContainer);
				const targetStationID = targetComponent.dropTargetEvent?.StationID;
				const draggedEvent = _.cloneDeep(targetComponent.draggedEvent);

				if (targetContainer === "hotkeys") {
					if (this.draggedElementData.SequenceNumber) {
						this.draggedElementData.SequenceNumber = parseFloat(this.draggedElementData.SequenceNumber);
						delete this.draggedElementData.Asset.POIPlaybackView2;
						delete this.draggedElementData.Asset.MusicPointData;
						this.getTargetComponent("hotkeys").addHotKey(this.draggedElementData);
					}
				}
				else if (targetContainer === "playlist" || targetContainer === "log") {
					if (targetComponent.dropTargetEvent?.EventID.startsWith("empty-playlist")) {
						const emptyPlaylists = this.$store.getters.getEmptyPlaylists(targetStationID);
						const emptyPlaylist = emptyPlaylists.find(
							p => p.PlaylistID === targetComponent.dropTargetEvent.PlaylistID
						);

						if (emptyPlaylist?.isEmptyPlaceholder) {
							const newPlaylistID = emptyPlaylist.PlaylistID.replace("empty-playlist-", "");
							this.$store.dispatch("createPlaylist", {
									PlaylistID: newPlaylistID,
									OrganizationID: this.$store.getters.getOrgID(),
									Title: moment(emptyPlaylist.ScheduledStartDateTime)
										.format("MM/DD/YYYY hh:mm:ss A [Hour]: HH"),
									StationID: targetStationID,
									Priority: 110,
									Origin: "LC",
									ScheduledStartDateTime: emptyPlaylist.ScheduledStartDateTime,
									PlaylistType: PlaylistTypes.Schedule
								})
							    .then(playlist => {
									this.$store.dispatch("addRemovePlaylistSubscriptions", {playlists: [playlist]});
									this.$store.commit("removeEmptyPlaylist", emptyPlaylist);
									this.$store.dispatch("createPlaylistEvent", {
										SequenceNumber: 1,
										AssetID: draggedEvent.AssetID,
										PlaylistID: newPlaylistID,
										EventType: EventTypes.PlayAsset,
										StatusCode: EventStatus.Ready,
										...(draggedEvent.Description && {Description: draggedEvent.Description}),
										Chain: draggedEvent.Chain ?? ChainTypes.Segue,
										ParentScheduledStartDateTime: emptyPlaylist.ScheduledStartDateTime,
										StationID: targetStationID,
										Skipped: false
									});
								});
						}
						else {
							this.$store.dispatch("addRemovePlaylistSubscriptions", {playlists: [emptyPlaylist]});
							this.$store.commit("removeEmptyPlaylist", emptyPlaylist);
							this.$store.dispatch("createPlaylistEvent", {
								SequenceNumber: 1,
								AssetID: draggedEvent.AssetID,
								PlaylistID: emptyPlaylist.PlaylistID,
								EventType: EventTypes.PlayAsset,
								StatusCode: EventStatus.Ready,
								...(draggedEvent.Description && {Description: draggedEvent.Description}),
								Chain: draggedEvent.Chain ?? ChainTypes.Segue,
								ParentScheduledStartDateTime: emptyPlaylist.ScheduledStartDateTime,
								StationID: targetStationID,
								Skipped: false
							});
						}
					}
					else {
						const targetEvent = _.cloneDeep(targetComponent.dropTargetEvent);
						const playlistId = targetEvent.ChildPlaylistID ?? targetEvent.PlaylistID;
						const playlist = this.$store.getters.getPlaylistById(playlistId);

						const moveSpotFeatureEnabled = this.$store.getters.isFeatureEnabled({
							object_id: targetStationID,
							reference: "zettacloud_event_move_spot"
						});

						const isSpotBlockPlaylist = playlist?.PlaylistType === PlaylistTypes.SpotBlock;
						const targetIsSpotBlock = !!(targetEvent?.insideSpotBlockPlaylist ||
						                             targetEvent?.ChildPlaylistID);

						const spotBlockEvents = isSpotBlockPlaylist
						                        ? playlist.Events.filter(e => !e.Skipped)
						                        : [];

						const dropToElement = ev.originalEvent.target.closest(`.${targetContainer}-event`);
						const isDroppingOnSpotBlockHeader = targetEvent.ChildPlaylistID &&
						                                    targetEvent.insideSpotBlockPlaylist &&
						                                    targetEvent.EventID === dropToElement?.id;
						const isFirstOrLastEvent = (
							isDroppingOnSpotBlockHeader ||
							_.last(spotBlockEvents)?.EventID === dropToElement?.id ||
							_.isEmpty(spotBlockEvents)
						);

						const isCopiedFromLibraryOrHotkey = _.isNil(draggedEvent.PlaylistID) ||
						                                    this.$store.getters.getPlaylistById(draggedEvent.PlaylistID)?.PlaylistType ===
						                                    PlaylistTypes.HotKeyBank;

						if (!moveSpotFeatureEnabled && targetIsSpotBlock &&
						    (!isCopiedFromLibraryOrHotkey || !isFirstOrLastEvent)) {
							this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
								message: "You can't move spots. The move spot feature is not enabled."
							});
							return;
						}
						const isDroppingOnSameElement = targetEvent && dropToElement?.id === targetEvent.EventID;
						const sequencerItem = this.$store.getters.getSequencerItemByEventID(
							targetStationID,
							draggedEvent?.EventID
						);
						const draggedEventIsCurrentOrPast = sequencerItem?.StatusCode === EventStatus.Current ||
						                                    sequencerItem?.StatusCode === EventStatus.Played;
						const nowPlayingEventID = _.findLast(
							this.$store.getters.getSequencerItems(targetStationID),
							e => e.StatusCode === EventStatus.Current
						)?.EventID;
						let isRemainingTimeRestricted;
						if (nowPlayingEventID === targetEvent?.EventID) {
							isRemainingTimeRestricted = this.$store.getters.getSequencerItemByEventID(
								targetStationID,
								nowPlayingEventID
							)?.RemainingTime < 5;
						}

						if (targetEvent && draggedEvent && !draggedEventIsCurrentOrPast &&
						    !isRemainingTimeRestricted && !isDroppingOnSameElement) {
							targetComponent.dropTargetEvent.blink = true;

							targetComponent.pendingInsertedEventsPositions.push({
								relatedEventID: dropToElement?.id,
								position: "after",
								blink: true
							});

							this.$store.commit("setBlinkingEvent", {
								eventID: dropToElement?.id,
								status: true
							});

							const eventStatus = this.$store.getters.getEventStatus({
								stationID: targetEvent.StationID,
								eventID: targetEvent.EventID
							});

							if (eventStatus.isPast || eventStatus.isCurrent && targetEvent.position === "before") {
								this.$store.commit(
									"SHOW_NOTIFICATION_WITH_TYPE",
									{message: "You cannot drop the event in past airtime positions."}
								);

								this.resetDragAndDropState(targetComponent, targetEvent);

								return;
							}

							if (draggedEvent.isSpotBlock && targetEvent.insideSpotBlockPlaylist) {
								this.$store.commit(
									"SHOW_NOTIFICATION_WITH_TYPE",
									{message: "A spot block cannot be dropped inside another spot block."}
								);

								this.resetDragAndDropState(targetComponent, targetEvent);

								return;
							}

							const targetPlaylistID = targetEvent.insideSpotBlockPlaylist ?
							                         targetEvent.ChildPlaylistID ?? targetEvent.PlaylistID :
							                         targetEvent.PlaylistID;
							const targetPlaylist = this.$store.getters.getPlaylistById(
								{stationID: targetEvent.StationID, playlistID: targetPlaylistID}
							);
							const isPlaylistEmpty = this.$store.getters.isPlaylistEmpty(
								{stationID: targetEvent.StationID, playlistID: targetPlaylistID}
							);

							let overridePOIMarkup;
							if (dropToElement?.classList?.contains("spot-block") &&
							    draggedEvent.isVoiceTrack && !!this.draggedElementData.OverridePOIMarkup) {
								this.overrideSegueTransitionsModal.open = true;
								overridePOIMarkup = await new Promise(resolve => {
									this.overrideSegueTransitionsModal.resolve = resolve;
								});
								this.overrideSegueTransitionsModal = {
									open: false,
									resolve: null
								};
							}

							let newSequenceNumber = 1;
							if (!isPlaylistEmpty) {
								if (isDroppingOnSpotBlockHeader) {
									const spotBlockEvents = this.$store.getters.getPlaylistEvents(
										{stationID: targetEvent.StationID, playlistID: targetEvent.ChildPlaylistID}
									);

									newSequenceNumber = await this.$store.dispatch("generateSequenceNumber", {
																	  eventID: spotBlockEvents[0]?.EventID,
																	  afterEvent: false
																  })
									                              .then(response => response.data[0])
									                              .catch(error => {
																	  console.error(error);
																	  this.$store.commit(
																		  "SHOW_NOTIFICATION_WITH_TYPE",
																		  {message: "An error occurred when trying to generate the event's sequence number."}
																	  );

																	  this.$store.commit("setBlinkingEvent", {
																		  eventID: targetEvent.EventID,
																		  status: false
																	  });
																	  return null;
																  });
								}
								else {
									newSequenceNumber = await this.$store.dispatch(
																	  "generateSequenceNumber",
																	  {
																		  eventID: dropToElement?.id ??
											                                       targetEvent.originalTargetEvent?.EventID ??
											                                       targetEvent.EventID,
																		  afterEvent: true
																	  }
																  )
									                              .then(response => response.data[0])
									                              .catch(error => {
																	  console.error(error);
																	  this.$store.commit(
																		  "SHOW_NOTIFICATION_WITH_TYPE",
																		  {message: "An error occurred when trying to generate the event's sequence number."}
																	  );

																	  this.$store.commit("setBlinkingEvent", {
																		  eventID: dropToElement?.id ??
											                                       targetEvent.originalTargetEvent?.EventID ??
											                                       targetEvent.EventID,
																		  status: false
																	  });
																	  return null;
																  });
								}
							}

							if (newSequenceNumber !== null) {
								const draggedContainer = this.getContainer(ev);
								const dropToOtherPlaylist = draggedContainer === "playlist" &&
								                            draggedEvent.PlaylistID !== targetPlaylistID;
								const dropToOtherStationPlaylist = dropToOtherPlaylist &&
								                                   draggedEvent.StationID !== targetStationID;
								if (needToCreateEvent || dropToOtherPlaylist) {
									if (!dropToOtherStationPlaylist && dropToOtherPlaylist) {
										await this.$store.dispatch("deletePlaylistEvent", draggedEvent.EventID);
									}

									const assetTitle = draggedEvent?.Asset?.Title;

									this.$store.dispatch("createPlaylistEvent", {
										SequenceNumber: newSequenceNumber,
										AssetID: draggedEvent.AssetID,
										PlaylistID: targetPlaylistID,
										EventType: dropToOtherPlaylist ? draggedEvent.EventType : EventTypes.PlayAsset,
										StatusCode: EventStatus.Ready,
										...(draggedEvent.Description && {Description: draggedEvent.Description}),
										Chain: draggedEvent.Chain ?? ChainTypes.Segue,
										ParentScheduledStartDateTime: targetPlaylist.ScheduledStartDateTime,
										StationID: targetStationID,
										Skipped: false
									}).then(() => {
										this.$store.commit(
											"SHOW_SUCCESS_NOTIFICATION",
											{message: "Event successfully created!"}
										);

									}).catch(() => {
										this.$store.commit(
											"SHOW_NOTIFICATION_WITH_TYPE",
											{
												message: `An error occurred when trying to create an event for the 
												${assetTitle ?? ""} asset.`
											}
										);
									}).finally(() => {
										this.resetDragAndDropState(targetComponent, targetEvent);
									});
								}
								else {
									this.$store.dispatch("updatePlaylistEvent", {
										PlaylistID: targetPlaylistID,
										EventID: draggedEvent.EventID,
										SequenceNumber: newSequenceNumber,
										StatusCode: EventStatus.Ready,
										EditCode: EditCodes.ManualReorder,
										EditComment: this.$store.getters.getUserFullName(),
										...(overridePOIMarkup && {OverridePOIMarkup: null})
									}).then(() => {
										this.$store.commit(
											"SHOW_SUCCESS_NOTIFICATION",
											{message: "Event successfully updated!"}
										);
									}).catch((error) => {
										console.error(error);
										this.$store.commit(
											"SHOW_NOTIFICATION_WITH_TYPE",
											{message: "An error occurred when trying to update the event's position."}
										);
									}).finally(() => {
										this.resetDragAndDropState(targetComponent, targetEvent);
									});
								}
							}
							else {
								this.resetDragAndDropState(targetComponent, targetEvent);
							}
						}
						else {
							targetComponent.draggedEvent = null;
							targetComponent.dropTargetEvent = null;
							if (isRemainingTimeRestricted) {
								this.$store.commit(
									"SHOW_NOTIFICATION_WITH_TYPE",
									{message: "The action was not complete because of 4 seconds time restriction for changing the playing next item."}
								);
							}
						}
					}
				}
			}
			else {
				this.resetDragAndDropState();
			}

			this.cancelThrottled();
		},

		resetDragAndDropState(targetComponent, targetEvent) {
			if (targetComponent && targetEvent) {
				const index = targetComponent.pendingInsertedEventsPositions.findIndex(
					e => e.relatedEventID === targetEvent.EventID
				);
				if (index > -1) {
					targetComponent.pendingInsertedEventsPositions.splice(index, 1);
				}

				targetComponent.draggedEvent = null;
				targetComponent.dropTargetEvent = null;
				this.$store.commit("setBlinkingEvent", {
					eventID: targetEvent.EventID,
					status: false
				});
			}
			else {
				emitter.emit("playlist-set-dragged-event", null);
				emitter.emit("playlist-set-drop-target-event", null);
				this.getTargetComponent("hotkeys")?.toggleHighlightDroppableContainer();
				this.getTargetComponent("log")?.setDropTargetEvent(null);
				this.getTargetComponent("log")?.setDraggedEvent(null);
			}
		},

		handleDraggedClone(element) {
			this.draggedElementData = {
				Asset: element?.Asset ?? element,
				AssetID: element?.AssetID,
				PlaylistID: this.$store.getters.selectedPageId(),
				Color: this.$store.getters.getAssetTypeColor(element?.AssetTypeID) ?? null,
				EventType: element.EventType ?? EventTypes.PlayAsset,
				StatusCode: element.StatusCode ?? EventStatus.Ready,
				Description: element?.Description,
				Chain: element?.AssetTypeID ?
				       this.$store.getters.getAssetTypeById(element?.AssetTypeID).DefaultChainType : ChainTypes.Segue,
				Skipped: false,
				OverridePOIMarkup: element?.OverridePOIMarkup,
				StationID: element.StationID
			};

			return this.draggedElementData;
		},

		handleOnDragAction(targetComponent, targetContainer, draggedElement, targetElement) {
			if (draggedElement) {
				const isDraggingOverEmptyPlaylistContainer = targetElement.id.includes("empty-playlist");

				if (isDraggingOverEmptyPlaylistContainer ||
				    this.isDraggingOverCorrectElement(draggedElement, targetElement)) {
					targetComponent.targetContainer = targetContainer;
					this.fillComponentDraggedEvent(targetComponent, draggedElement);
					this.fillComponentTargetEvent(targetComponent, draggedElement, targetElement);

					if (targetElement.classList.contains("spot-block") ||
					    targetElement.classList.contains("local-break")) {
						if (!this.targetElementToExpand ||
						    targetElement.getAttribute("id") !== this.targetElementToExpand.getAttribute("id")) {
							this.targetElementToExpand = targetElement;
							clearTimeout(this.throttled);
							this.throttled = setTimeout(
								() => this.expandSpotBlockIfNeeded(this.targetElementToExpand),
								2000
							);
						}
					}
					else {
						this.cancelThrottled();
					}
				}
				else {
					this.cancelThrottled();
				}
				this.scrollContainerIfNeeded(draggedElement, targetContainer);
				this.getTargetComponent("hotkeys")?.toggleHighlightDroppableContainer(this.draggedElementData);
			}
		},

		fillComponentTargetEvent(component, draggedElement, targetElement) {
			const playlistID = targetElement.dataset.playlistId;
			const stationID = targetElement.dataset.stationId;
			component.dropTargetEvent = {
				EventID: targetElement.id,
				PlaylistID: playlistID,
				StationID: stationID,
				ChildPlaylistID: targetElement.dataset.childPlaylistId,
				position: draggedElement.getBoundingClientRect().top - targetElement.getBoundingClientRect().top >
				          0 ?
				          "after" : "before",
				insideSpotBlockPlaylist: targetElement.classList.contains("spot") ||
				                         targetElement.classList.contains("empty-spot-placeholder") ||
				                         targetElement.classList.contains("v-expansion-panel--active")
			};

			// in case of dragging over an empty playlist container, we need to find the proper event to insert it before or after
			const targetElementIsEmptyPlaylistContainer = targetElement.id.includes("empty-playlist");

			if (component.targetContainer === "playlist" && targetElementIsEmptyPlaylistContainer) {
				const TOHPlaylist = component.playlistsList.find(
					p => p.PlaylistID === playlistID
				);

				let originalTargetEvent;
				let originalTargetPosition = component.dropTargetEvent.position;

				if (component.dropTargetEvent.position === "before") {
					originalTargetEvent = TOHPlaylist?.TopOfTheHour?.previousEvent;
					originalTargetPosition = "after";
				}
				else if (!this.$store.getters.isPlaylistEmpty({stationID, playlistID})) {
					originalTargetEvent = TOHPlaylist.TopOfTheHour.nextEvent ??
					                      TOHPlaylist.TopOfTheHour.previousEvent;
					originalTargetPosition = TOHPlaylist.TopOfTheHour.nextEvent ? "before" : "after";
				}

				if (originalTargetEvent) {
					component.dropTargetEvent.PlaylistID = originalTargetEvent.PlaylistID;
					component.dropTargetEvent.originalTargetEvent = {
						EventID: originalTargetEvent.EventID,
						position: originalTargetPosition
					};
				}
			}
		},

		fillComponentDraggedEvent(component, draggedElement) {
			component.draggedEvent = {
				EventID: draggedElement.id,
				PlaylistID: draggedElement.dataset.playlistId,
				AssetID: this.draggedElementData?.AssetID,
				AssetTitle: this.draggedElementData?.Asset?.Title,
				EventType: this.draggedElementData.EventType,
				Description: this.draggedElementData.Description,
				Chain: this.draggedElementData.Chain,
				isSpotBlock: draggedElement.classList.contains("spot-block"),
				isVoiceTrack: draggedElement.classList.contains("voice-track"),
				StationID: this.draggedElementData.StationID
			};
		},

		isDraggingOverCorrectElement(draggedElement, targetElement) {
			return Math.abs(
				draggedElement.getBoundingClientRect().top - targetElement.getBoundingClientRect().top
			) <= this.emptyInsertThreshold;
		},

		getTargetComponent(targetContainer) {
			if (targetContainer === "playlist") {
				return this.$store.getters.getContainerByRef("PlaylistComponent");
			}

			if (targetContainer === "log") {
				return this.$store.getters.getContainerByRef("StationLogComponent");
			}

			if (targetContainer === "hotkeys") {
				return this.$store.getters.getContainerByRef("HotKeysComponent");
			}
		},

		expandSpotBlockIfNeeded(targetElement) {
			if (!targetElement.classList.contains("v-expansion-panel--active")) {
				targetElement.querySelector("button.v-expansion-panel-title").click();
			}
		},

		scrollContainerIfNeeded(draggedElement, targetContainer) {
			if (this.$refs.scrollbar) {
				const container = document.getElementById(`${targetContainer}-events-container`);
				const draggedElementRect = draggedElement.getBoundingClientRect();
				const containerRect = container.getBoundingClientRect();
				if (draggedElementRect.bottom >= containerRect.bottom - this.emptyInsertThreshold) {     // reached bottom
					this.$refs.scrollbar.$el.scrollBy(0, 50);
				}
				else if (draggedElementRect.top - containerRect.top <= this.emptyInsertThreshold) {      // reached top
					this.$refs.scrollbar.$el.scrollBy(0, -50);
				}
			}
		},

		isDraggableEvent(event) {
			const eventStatus = this.$store.getters.getEventStatus({event});
			if (event?.Asset?.KillDateTime) {
				return !eventStatus.isCurrent && !eventStatus.isPast &&
				       moment().isBefore(event?.Asset?.KillDateTime);
			}
			return !eventStatus.isCurrent && !eventStatus.isPast && !(event?.EventType === EventTypes.DVTBlock);
		},

		loadDropTargetEvent(eventID) {
			return this.dropTargetEvent?.EventID === eventID ? this.dropTargetEvent :
			       this.pendingInsertedEventsPositions.find(e => e.relatedEventID === eventID);
		},

		getContainer(event) {
			return event.target?.closest(".drop-container")?.getAttribute("data-drop-container");
		},

		cancelDraggingOrCutOrCopyOnEscape(ev) {
			if (ev.key === "Escape") {
				const draggedElement = document.querySelector(`.dragged-${this.componentName}`);

				if (draggedElement) {
					draggedElement.remove();
					this.draggedElementData = null;
					this.draggedEvent = null;
					this.dropTargetEvent = null;
				}

				this.$store.commit("setCopiedEvent", null);
				this.$store.commit("setCuttingEvent", null);

				this.cancelThrottled();
			}
		},

		cancelThrottled() {
			if (this.targetElementToExpand) {
				this.targetElementToExpand = null;
				clearTimeout(this.throttled);
			}
		}
	}
}

export default dragAndDropMixin;